import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  Label,
  Spinner,
  Subtitle1,
  Text,
} from '@fluentui/react-components';
import { DeleteRegular } from '@fluentui/react-icons';
import { QRCodeSVG } from 'qrcode.react';
import { authClient } from '../../utils/auth-client';
import { useBestaetigen } from "../../components/redesign/bestaetigen";

type Passkey = {
  id: string;
  name?: string | null;
  createdAt?: string | Date | null;
};

/**
 * User-facing controls for second factors:
 *  - List, add, rename, delete passkeys (WebAuthn)
 *  - Enable / disable TOTP (with QR + verify), backup codes
 *
 * Both blocks are independent — having one doesn't preclude the other.
 */
export const SecuritySettings: React.FC = () => {
  const { bestaetigen } = useBestaetigen();
  const [passkeys, setPasskeys] = useState<Passkey[]>([]);
  const [passkeysLoading, setPasskeysLoading] = useState(false);
  const [passkeyError, setPasskeyError] = useState<string | null>(null);

  const [twoFactorEnabled, setTwoFactorEnabled] = useState<boolean | null>(null);
  const [twoFactorBusy, setTwoFactorBusy] = useState(false);

  // Enable-2FA wizard
  const [enableDialogOpen, setEnableDialogOpen] = useState(false);
  const [enablePassword, setEnablePassword] = useState('');
  const [totpUri, setTotpUri] = useState<string | null>(null);
  const [totpSecret, setTotpSecret] = useState<string | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [enableError, setEnableError] = useState<string | null>(null);

  // Disable-2FA dialog
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableError, setDisableError] = useState<string | null>(null);

  const loadPasskeys = async () => {
    setPasskeysLoading(true);
    setPasskeyError(null);
    try {
      const result = await (authClient as any).passkey.listUserPasskeys();
      const list: Passkey[] = result?.data ?? result?.passkeys ?? [];
      setPasskeys(Array.isArray(list) ? list : []);
    } catch (err) {
      setPasskeyError(err instanceof Error ? err.message : 'Passkeys konnten nicht geladen werden.');
    } finally {
      setPasskeysLoading(false);
    }
  };

  const loadSession = async () => {
    try {
      const res = await authClient.getSession();
      const user: any = (res as any)?.data?.user ?? null;
      setTwoFactorEnabled(Boolean(user?.twoFactorEnabled));
    } catch {
      setTwoFactorEnabled(null);
    }
  };

  useEffect(() => {
    loadPasskeys();
    loadSession();
  }, []);

  const handleAddPasskey = async () => {
    setPasskeyError(null);
    try {
      const result = await (authClient as any).passkey.addPasskey({
        name: `Passkey vom ${new Date().toLocaleDateString('de-DE')}`,
      });
      if (result?.error) {
        setPasskeyError(result.error.message || 'Passkey konnte nicht hinzugefügt werden.');
        return;
      }
      await loadPasskeys();
    } catch (err) {
      setPasskeyError(err instanceof Error ? err.message : 'Passkey-Registrierung wurde abgebrochen.');
    }
  };

  const handleDeletePasskey = async (id: string) => {
    const ok = await bestaetigen({
      titel: 'Passkey löschen?',
      text: 'Eine Anmeldung mit diesem Passkey ist danach nicht mehr möglich.',
      ja: 'Löschen',
      ton: 'danger',
    });
    if (!ok) return;
    setPasskeyError(null);
    try {
      const result = await (authClient as any).passkey.deletePasskey({ id });
      if (result?.error) {
        setPasskeyError(result.error.message || 'Passkey konnte nicht gelöscht werden.');
        return;
      }
      await loadPasskeys();
    } catch (err) {
      setPasskeyError(err instanceof Error ? err.message : 'Löschen fehlgeschlagen.');
    }
  };

  const openEnableDialog = () => {
    setEnablePassword('');
    setTotpUri(null);
    setTotpSecret(null);
    setBackupCodes(null);
    setVerifyCode('');
    setEnableError(null);
    setEnableDialogOpen(true);
  };

  const handleEnableTotpStart = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!enablePassword) return;
    setTwoFactorBusy(true);
    setEnableError(null);
    try {
      const result = await (authClient as any).twoFactor.enable({ password: enablePassword });
      if (result?.error) {
        setEnableError(result.error.message || 'Aktivierung fehlgeschlagen.');
        return;
      }
      const data = result?.data ?? result;
      setTotpUri(data?.totpURI ?? data?.totpUri ?? null);
      setTotpSecret(data?.secret ?? null);
      setBackupCodes(Array.isArray(data?.backupCodes) ? data.backupCodes : null);
    } catch (err) {
      setEnableError(err instanceof Error ? err.message : 'Aktivierung fehlgeschlagen.');
    } finally {
      setTwoFactorBusy(false);
    }
  };

  const handleVerifyTotp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!verifyCode.trim()) return;
    setTwoFactorBusy(true);
    setEnableError(null);
    try {
      const result = await (authClient as any).twoFactor.verifyTotp({ code: verifyCode.trim() });
      if (result?.error) {
        setEnableError(result.error.message || 'Code wurde nicht akzeptiert.');
        return;
      }
      setEnableDialogOpen(false);
      await loadSession();
    } catch (err) {
      setEnableError(err instanceof Error ? err.message : 'Verifikation fehlgeschlagen.');
    } finally {
      setTwoFactorBusy(false);
    }
  };

  const handleDisable = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!disablePassword) return;
    setTwoFactorBusy(true);
    setDisableError(null);
    try {
      const result = await (authClient as any).twoFactor.disable({ password: disablePassword });
      if (result?.error) {
        setDisableError(result.error.message || 'Deaktivierung fehlgeschlagen.');
        return;
      }
      setDisableDialogOpen(false);
      setDisablePassword('');
      await loadSession();
    } catch (err) {
      setDisableError(err instanceof Error ? err.message : 'Deaktivierung fehlgeschlagen.');
    } finally {
      setTwoFactorBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', maxWidth: '720px' }}>
      <Card style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <Subtitle1>Passkeys</Subtitle1>
        <Text size={200} style={{ color: '#605e5c' }}>
          Mit Passkeys (WebAuthn) meldest du dich biometrisch oder über Hardware-Token an —
          ohne Passwort und ohne TOTP-Code.
        </Text>

        {passkeyError && (
          <div style={{ padding: '12px', backgroundColor: '#fde7e9', color: '#a80000', borderRadius: '4px' }}>
            <Text>{passkeyError}</Text>
          </div>
        )}

        {passkeysLoading ? (
          <Spinner size="tiny" />
        ) : passkeys.length === 0 ? (
          <Text size={200} style={{ fontStyle: 'italic' }}>Noch keine Passkeys registriert.</Text>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {passkeys.map((pk) => (
              <div
                key={pk.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 12px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px',
                }}
              >
                <Text>{pk.name || 'Unbenannter Passkey'}</Text>
                <Button
                  appearance="subtle"
                  icon={<DeleteRegular />}
                  onClick={() => handleDeletePasskey(pk.id)}
                  size="small"
                >
                  Löschen
                </Button>
              </div>
            ))}
          </div>
        )}

        <div>
          <Button appearance="primary" onClick={handleAddPasskey}>
            Neuen Passkey hinzufügen
          </Button>
        </div>
      </Card>

      <Card style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <Subtitle1>Zwei-Faktor-Authentifizierung (TOTP)</Subtitle1>
        <Text size={200} style={{ color: '#605e5c' }}>
          Nach erfolgreichem Login wird zusätzlich ein 6-stelliger Code aus deiner
          Authenticator-App (z.B. Microsoft Authenticator, 1Password, Authy) abgefragt.
        </Text>

        {twoFactorEnabled === null ? (
          <Spinner size="tiny" />
        ) : twoFactorEnabled ? (
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <Text style={{ color: '#107c10', fontWeight: 600 }}>Aktiv</Text>
            <Button appearance="secondary" onClick={() => setDisableDialogOpen(true)}>
              Deaktivieren
            </Button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <Text style={{ color: '#605e5c' }}>Nicht aktiviert</Text>
            <Button appearance="primary" onClick={openEnableDialog}>
              Aktivieren
            </Button>
          </div>
        )}
      </Card>

      <Dialog open={enableDialogOpen} onOpenChange={(_e, data) => setEnableDialogOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>TOTP aktivieren</DialogTitle>
            <DialogContent>
              {!totpUri ? (
                <form onSubmit={handleEnableTotpStart} style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '12px' }}>
                  <Text size={200}>
                    Bitte bestätige mit deinem Passwort, dass du TOTP aktivieren willst.
                  </Text>
                  <Label htmlFor="enablePwd">Passwort</Label>
                  <Input
                    id="enablePwd"
                    type="password"
                    value={enablePassword}
                    onChange={(_e, data) => setEnablePassword(data.value)}
                    required
                    autoFocus
                  />
                  {enableError && (
                    <div style={{ padding: '8px', backgroundColor: '#fde7e9', color: '#a80000', borderRadius: '4px' }}>
                      <Text>{enableError}</Text>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
                    <Button appearance="secondary" onClick={() => setEnableDialogOpen(false)} disabled={twoFactorBusy}>
                      Abbrechen
                    </Button>
                    <Button type="submit" appearance="primary" disabled={twoFactorBusy || !enablePassword}>
                      {twoFactorBusy ? <Spinner size="tiny" /> : 'Weiter'}
                    </Button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleVerifyTotp} style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '12px' }}>
                  <Text size={200}>
                    Scanne den QR-Code mit deiner Authenticator-App und gib anschließend den
                    angezeigten 6-stelligen Code ein, um die Einrichtung abzuschließen.
                  </Text>
                  <div style={{ display: 'flex', justifyContent: 'center', backgroundColor: '#ffffff', padding: '16px' }}>
                    <QRCodeSVG value={totpUri} size={192} />
                  </div>
                  {totpSecret && (
                    <Text size={100} style={{ fontFamily: 'monospace', textAlign: 'center', color: '#605e5c' }}>
                      Manuell: {totpSecret}
                    </Text>
                  )}
                  {backupCodes && backupCodes.length > 0 && (
                    <div style={{ padding: '12px', backgroundColor: '#fff4ce', borderRadius: '4px' }}>
                      <Text weight="semibold" size={200}>Backup-Codes (jetzt sicher speichern, jeweils einmal nutzbar):</Text>
                      <div style={{ fontFamily: 'monospace', fontSize: '13px', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '4px 12px', marginTop: '8px' }}>
                        {backupCodes.map((c) => <span key={c}>{c}</span>)}
                      </div>
                    </div>
                  )}
                  <Label htmlFor="verifyCode">Code aus der Authenticator-App</Label>
                  <Input
                    id="verifyCode"
                    value={verifyCode}
                    onChange={(_e, data) => setVerifyCode(data.value)}
                    required
                    inputMode="numeric"
                    autoFocus
                    style={{ letterSpacing: '0.3em' }}
                  />
                  {enableError && (
                    <div style={{ padding: '8px', backgroundColor: '#fde7e9', color: '#a80000', borderRadius: '4px' }}>
                      <Text>{enableError}</Text>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                    <Button appearance="secondary" onClick={() => setEnableDialogOpen(false)} disabled={twoFactorBusy}>
                      Schließen
                    </Button>
                    <Button type="submit" appearance="primary" disabled={twoFactorBusy || !verifyCode.trim()}>
                      {twoFactorBusy ? <Spinner size="tiny" /> : 'Bestätigen'}
                    </Button>
                  </div>
                </form>
              )}
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Dialog open={disableDialogOpen} onOpenChange={(_e, data) => setDisableDialogOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>TOTP deaktivieren</DialogTitle>
            <DialogContent>
              <form onSubmit={handleDisable} style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '12px' }}>
                <Text size={200}>Bitte bestätige mit deinem Passwort.</Text>
                <Label htmlFor="disablePwd">Passwort</Label>
                <Input
                  id="disablePwd"
                  type="password"
                  value={disablePassword}
                  onChange={(_e, data) => setDisablePassword(data.value)}
                  required
                  autoFocus
                />
                {disableError && (
                  <div style={{ padding: '8px', backgroundColor: '#fde7e9', color: '#a80000', borderRadius: '4px' }}>
                    <Text>{disableError}</Text>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                  <Button appearance="secondary" onClick={() => setDisableDialogOpen(false)} disabled={twoFactorBusy}>
                    Abbrechen
                  </Button>
                  <Button type="submit" appearance="primary" disabled={twoFactorBusy || !disablePassword}>
                    {twoFactorBusy ? <Spinner size="tiny" /> : 'Deaktivieren'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default SecuritySettings;
